import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * @constant {number} EVENT_CUTOFF_SECONDS - how recently (in period seconds) a
 * goal/behind must have happened to still be shown as a live score-update pill.
 */
const EVENT_CUTOFF_SECONDS = 15;
const CLEAR_AFTER_MS = 4000;

/**
 * Detects a recent goal/behind so the status badge can animate a club-coloured
 * score update. The scoring sibling to usePeriodEvent.
 *
 * @param {string} matchId - The providerId/matchId to watch scoring events for.
 * @returns {object} { show, teamId, type: 'GOAL'|'BEHIND'|'RUSHED_BEHIND' }
 */
export function useScoreUpdate(matchId) {
    const [update, setUpdate] = useState({ show: false });

    const wormData = useSelector((state) =>
        PULSE.app.redux.selectors.match.scoreWorm(state, matchId)
    );
    const matchClock = useSelector((state) =>
        PULSE.app.redux.selectors.match.matchClock(state, matchId)
    );
    const isActuallyLive = useSelector((state) =>
        PULSE.app.redux.selectors.match.isActuallyLive(state, matchId)
    );

    useEffect(() => {
        const hasScoringEvent =
            isActuallyLive && wormData?.scoringEvents?.length && matchClock;

        if (hasScoringEvent) {
            const latestGoal =
                wormData.scoringEvents[wormData.scoringEvents.length - 1];
            const latestPeriod = matchClock.periods?.[0];
            const isSamePeriod =
                latestPeriod &&
                latestPeriod.periodNumber === latestGoal.periodNumber;
            const isRecent =
                isSamePeriod &&
                latestGoal.periodSeconds >
                    latestPeriod.periodSeconds - EVENT_CUTOFF_SECONDS;

            if (isRecent) {
                setUpdate({
                    show: true,
                    teamId: latestGoal.teamId,
                    type: latestGoal.scoreType
                });

                const timeout = setTimeout(() => {
                    setUpdate({ show: false });
                }, CLEAR_AFTER_MS);

                return () => clearTimeout(timeout);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wormData?.scoringEvents?.length]);

    return update;
}
